import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  max_rooms: number;
  features: string[];
  is_active: boolean;
}

interface HotelSubscription {
  id: string;
  hotel_id: string;
  plan_id: string;
  status: string;
  started_at: string;
  expires_at: string | null;
  plan: SubscriptionPlan;
}

interface CachedSubscription {
  data: HotelSubscription | null;
  hotelId: string;
  timestamp: number;
}

const CACHE_KEY = 'hotel3d_current_subscription_cache';
const CACHE_DURATION_MS = 15 * 60 * 1000; // 15 минут (ранее было 24ч — вызывало залипание при "мигании" статуса)

const getCachedSubscription = (hotelId: string): HotelSubscription | null | undefined => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return undefined; // undefined = нет кэша
    
    const parsed: CachedSubscription = JSON.parse(cached);
    const now = Date.now();
    
    // Проверяем валидность кэша
    if (parsed.hotelId === hotelId && (now - parsed.timestamp) < CACHE_DURATION_MS) {
      console.log('⚡ Используем кэшированную подписку (осталось:', 
        Math.round((CACHE_DURATION_MS - (now - parsed.timestamp)) / 1000 / 60), 'мин)');
      return parsed.data; // null = нет подписки, объект = есть подписка
    }
    
    return undefined;
  } catch {
    return undefined;
  }
};

const setCachedSubscription = (hotelId: string, data: HotelSubscription | null): void => {
  try {
    const cache: CachedSubscription = {
      data,
      hotelId,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    console.log('💾 Кэшировали подписку на 24 часа');
  } catch (e) {
    console.warn('Не удалось кэшировать подписку:', e);
  }
};

export const clearSubscriptionPlanCache = () => {
  localStorage.removeItem(CACHE_KEY);
  console.log('🗑️ Кэш подписки очищен');
};

export const useSubscriptionPlans = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<HotelSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const { toast } = useToast();
  const { currentHotel } = useOptimizedHotel();
  const forceRefreshRef = useRef(false);

  const fetchPlans = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly');

      if (error) throw error;
      
      const formattedPlans = (data || []).map(plan => ({
        ...plan,
        features: Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features as string)
      }));
      
      setPlans(formattedPlans);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      toast({
        title: "Ошибка",
        description: "Не удалось загрузить тарифные планы",
        variant: "destructive",
      });
    }
  }, [toast]);

  const fetchCurrentSubscription = useCallback(async (forceRefresh = false) => {
    const hotelId = currentHotel?.id;
    
    if (!hotelId) {
      // Fallback на RPC если контекст еще не загружен
      try {
        const { data: currentHotelId, error: hotelError } = await supabase
          .rpc('get_current_hotel_id');
        
        if (hotelError || !currentHotelId) {
          console.log('No current hotel ID found');
          setCurrentSubscription(null);
          setLoading(false);
          return;
        }

        await fetchSubscriptionForHotel(currentHotelId, forceRefresh);
      } catch (error) {
        console.error('Error getting current hotel ID:', error);
        setCurrentSubscription(null);
        setLoading(false);
      }
      return;
    }

    await fetchSubscriptionForHotel(hotelId, forceRefresh);
  }, [currentHotel?.id]);

  const fetchSubscriptionForHotel = async (hotelId: string, forceRefresh = false) => {
    // Проверяем кэш, если не форсированное обновление
    if (!forceRefresh) {
      const cached = getCachedSubscription(hotelId);
      if (cached !== undefined) {
        setCurrentSubscription(cached);
        setLoading(false);
        return;
      }
    }

    try {
      console.log('🔄 Загружаем подписку из БД для отеля:', hotelId);

      // ⚡ AK-47: Единая правда — действующая подписка определяется по expires_at,
      // а не по status. Статус 'pending' (оплата в обработке ЮKassa) при валидном
      // expires_at НЕ должен блокировать доступ. Берём последнюю запись отеля.
      const { data, error } = await supabase
        .from('hotel_subscriptions')
        .select(`
          *,
          plan:subscription_plans(*)
        `)
        .eq('hotel_id', hotelId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription:', error);
        throw error;
      }
      
      // Считаем подписку действующей, если expires_at в будущем или null (бессрочная).
      const isEffective = !!data && (
        data.expires_at === null || new Date(data.expires_at) > new Date()
      );

      if (data && isEffective) {
        console.log('Found effective subscription (status:', data.status, ', expires_at:', data.expires_at, ')');
        const formattedData = {
          ...data,
          plan: {
            ...data.plan,
            features: Array.isArray(data.plan.features) ? data.plan.features : JSON.parse(data.plan.features as string)
          }
        };
        setCurrentSubscription(formattedData);
        setCachedSubscription(hotelId, formattedData);
      } else {
        console.log('No effective subscription found for hotel:', hotelId, '(data:', data, ')');
        setCurrentSubscription(null);
        setCachedSubscription(hotelId, null);
      }
    } catch (error) {
      console.error('Error fetching current subscription:', error);
      setCurrentSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToPlan = async (planId: string) => {
    if (isSubscribing) {
      console.log('Subscription already in progress, ignoring');
      return;
    }

    setIsSubscribing(true);
    
    try {
      console.log('=== STARTING SUBSCRIPTION PROCESS ===');
      console.log('Plan ID:', planId);
      
      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError || !user.user) {
        console.error('User authentication error:', userError);
        throw new Error('Пользователь не авторизован');
      }
      console.log('Authenticated user:', user.user.id);

      // Используем currentHotel из контекста
      const hotelId = currentHotel?.id;
      
      if (!hotelId) {
        // Fallback на RPC
        const { data: currentHotelId, error: hotelIdError } = await supabase
          .rpc('get_current_hotel_id');
        
        if (hotelIdError || !currentHotelId) {
          throw new Error('Отель не найден');
        }
        
        await processSubscription(currentHotelId, planId);
      } else {
        await processSubscription(hotelId, planId);
      }

    } catch (error: any) {
      console.error('=== SUBSCRIPTION ERROR ===');
      console.error('Error details:', error);
      
      let errorMessage = 'Не удалось изменить тарифный план';
      
      if (error.code === '23505') {
        errorMessage = 'Подписка для этого отеля уже существует. Попробуйте обновить страницу.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Ошибка",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  const processSubscription = async (hotelId: string, planId: string) => {
    console.log('Using hotel ID:', hotelId);

    const { data: existingSubscriptions, error: checkError } = await supabase
      .from('hotel_subscriptions')
      .select('id, status, plan_id')
      .eq('hotel_id', hotelId);

    if (checkError) {
      console.error('Error checking existing subscriptions:', checkError);
      throw checkError;
    }

    console.log('Existing subscriptions found:', existingSubscriptions);

    let subscriptionResult;

    if (existingSubscriptions && existingSubscriptions.length > 0) {
      console.log('Updating existing subscription...');
      subscriptionResult = await supabase
        .from('hotel_subscriptions')
        .update({
          plan_id: planId,
          status: 'active',
          started_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('hotel_id', hotelId)
        .select(`
          *,
          plan:subscription_plans(name)
        `);
    } else {
      console.log('Creating new subscription...');
      subscriptionResult = await supabase
        .from('hotel_subscriptions')
        .insert({
          hotel_id: hotelId,
          plan_id: planId,
          status: 'active',
          started_at: new Date().toISOString()
        })
        .select(`
          *,
          plan:subscription_plans(name)
        `);
    }

    if (subscriptionResult.error) {
      console.error('Subscription operation error:', subscriptionResult.error);
      throw subscriptionResult.error;
    }

    console.log('Subscription operation successful:', subscriptionResult.data);

    // ⚡ Очищаем кэш после изменения подписки
    clearSubscriptionPlanCache();
    
    // Также очищаем кэш лимитов
    localStorage.removeItem('hotel3d_subscription_limits_cache');

    await fetchCurrentSubscription(true); // Принудительное обновление
    
    const planName = subscriptionResult.data?.[0]?.plan?.name || 'выбранный план';
    
    toast({
      title: "Успешно",
      description: `Тарифный план "${planName}" успешно активирован`,
    });

    console.log('=== SUBSCRIPTION PROCESS COMPLETED ===');
  };

  // Загружаем планы один раз
  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Перезагружаем подписку при смене отеля
  useEffect(() => {
    setLoading(true);
    fetchCurrentSubscription(forceRefreshRef.current);
    forceRefreshRef.current = false;
  }, [currentHotel?.id, fetchCurrentSubscription]);

  return {
    plans,
    currentSubscription,
    loading,
    isSubscribing,
    subscribeToPlan,
    refetch: () => {
      fetchPlans();
      forceRefreshRef.current = true;
      fetchCurrentSubscription(true); // Принудительное обновление
    },
    clearCache: clearSubscriptionPlanCache
  };
};